import { jsonParse, jsonStringify } from '@lyearn/core/utils/json';
import localStorage from '@/modules/lightspeed/utils/localStorage';

export enum LessonOrderPreferenceEnum {
  AddToBottom = 'ADD_TO_BOTTOM',
  AddToTop = 'ADD_TO_TOP',
}

const STORAGE_KEY = 'lessonOrderPreference';

/**
 * Get all lesson order preferences from localStorage
 */
const getAllPreferences = (): Record<string, LessonOrderPreferenceEnum> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const { data, error } = jsonParse<Record<string, LessonOrderPreferenceEnum>>(stored);
      if (error) {
        console.error('Error parsing lesson order preferences:', error);
        return {};
      }
      return data || {};
    }
  } catch (error) {
    console.error('Error retrieving lesson order preferences:', error);
  }
  return {};
};

/**
 * Save all lesson order preferences to localStorage
 */
const saveAllPreferences = (preferences: Record<string, LessonOrderPreferenceEnum>): void => {
  try {
    const { data, error } = jsonStringify(preferences);
    if (error) {
      console.error('Error stringifying lesson order preferences:', error);
      return;
    }
    if (data) {
      localStorage.setItem(STORAGE_KEY, data);
    }
  } catch (error) {
    console.error('Error saving lesson order preferences:', error);
  }
};

/**
 * Get lesson order preference for a specific course
 */
export const getLessonOrderPreference = (courseId?: string): LessonOrderPreferenceEnum => {
  if (!courseId) return LessonOrderPreferenceEnum.AddToBottom;

  const preferences = getAllPreferences();
  return preferences[courseId] || LessonOrderPreferenceEnum.AddToBottom;
};

/**
 * Set lesson order preference for a specific course
 */
export const setLessonOrderPreference = (
  courseId: string,
  preference: LessonOrderPreferenceEnum,
): void => {
  const preferences = getAllPreferences();
  preferences[courseId] = preference;
  saveAllPreferences(preferences);
};

/**
 * Reset lesson order preference to "Add to Bottom" for a specific course
 * Used when drip settings change from Immediate to non-Immediate
 */
export const resetLessonOrderPreferenceToBottom = (courseId: string): void => {
  const preferences = getAllPreferences();
  if (preferences[courseId] === LessonOrderPreferenceEnum.AddToTop) {
    preferences[courseId] = LessonOrderPreferenceEnum.AddToBottom;
    saveAllPreferences(preferences);
  }
};
